/**
 * Глобальный поиск (C-28) — круглая кнопка рядом с панелью вкладок, как в Telegram.
 *
 * Одно поле, две секции: контакты по имени или никнейму (локально, из реплики)
 * и люди по никнейму на сервере (`GET /users/search`, от 5 символов, BS §11.3).
 * Поле получает фокус сразу — экран открывают, чтобы печатать.
 *
 * Пустое поле показывает не объяснение правил поиска, а «Недавние» — тех, кого
 * уже открывали (`UiKey.searchRecent`): в мессенджерах это самая частая причина
 * зайти в поиск второй раз.
 */
import { useNavigate, useSearch } from '@tanstack/react-router'
import { useEffect, useMemo, useRef, useState } from 'react'

import { contactDisplayName, useContacts, type Contact } from '@entities/contact/model'
import { useConversations } from '@entities/conversation/model'
import { faceOf } from '@entities/conversation/display'
import { useMessageSearch } from '@features/chat/use-message-search'
import { subscribersLabel } from '@entities/conversation/display'
import { useChannelSearch, useUsernameSearch } from '@entities/user/username-search'
import type { UserSearchResult } from '@shared/api/types'
import { UiKey, uiStateRepo, useReplica } from '@shared/db'
import { usernameRules, usernameSearchQuery } from '@shared/lib/username'
import { Avatar, Banner, Button, EmptyState, Icon, SearchInput, cx } from '@shared/ui'

import { KEY_ITEM_ATTR, KEY_LIST_ATTR } from '../../model/hotkeys'
import { Screen } from '../../layouts/Screen'
import { formatPresence } from '../../lib/format'
import { useAppStore } from '../../model/app-store'
import { useLivePresence } from '../../model/presence-store'
import rows from '../contacts/contacts.module.css'
import styles from './search.module.css'
import roadmap from '../chats/roadmap.module.css'
import { mediaLabel } from '../chats/message-text'

const collator = new Intl.Collator('ru', { sensitivity: 'base' })

/** Недавно открытые из поиска. Фото не храним — берём из реплики, если человек в контактах. */
interface RecentEntry {
  id: string
  name: string
  username: string | null
}

const RECENT_MAX = 8

async function rememberRecent(entry: RecentEntry): Promise<void> {
  const stored = (await uiStateRepo.get<RecentEntry[]>(UiKey.searchRecent)) ?? []
  const next = [entry, ...stored.filter((item) => item.id !== entry.id)].slice(0, RECENT_MAX)
  await uiStateRepo.set(UiKey.searchRecent, next)
}

function matchesContact(contact: Contact, needle: string): boolean {
  const name = contactDisplayName(contact).toLowerCase()
  const username = (contact.username ?? '').toLowerCase()
  return name.includes(needle) || (Boolean(username) && username.includes(needle.replace(/^@/, '')))
}

export function SearchScreen(): React.ReactElement {
  const navigate = useNavigate()
  const search = useSearch({ strict: false }) as { q?: string }
  const [query, setQuery] = useState(search.q ?? '')
  const inputRef = useRef<HTMLInputElement>(null)
  const [category, setCategory] = useState<'people' | 'chats' | 'messages'>('people')
  const [sender, setSender] = useState('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [attachment, setAttachment] = useState<'' | 'image' | 'video' | 'voice' | 'file' | 'links'>('')
  const { items: conversations } = useConversations('all')
  const messageSearch = useMessageSearch(
    category === 'messages'
      ? {
          q: query.trim(),
          ...(sender ? { sender_id: sender } : {}),
          ...(from ? { from: new Date(`${from}T00:00:00`).toISOString() } : {}),
          ...(to ? { to: new Date(`${to}T23:59:59.999`).toISOString() } : {}),
          ...(attachment === 'links' ? { has_link: true } : attachment ? { type: attachment } : {}),
        }
      : {},
  )
  const conversationNames = new Map(conversations.map((c) => [c.id, faceOf(c).name ?? 'Чат']))

  useEffect(() => {
    // Фокус после входа экрана: раньше поле получило бы его до анимации и клавиатура сжала бы кадр.
    // Без прокрутки: Safari иначе прокручивал документ к полю, пока экран ещё въезжает справа.
    const timer = setTimeout(() => inputRef.current?.focus({ preventScroll: true }), 80)
    return () => clearTimeout(timer)
  }, [])

  const { items: contacts } = useContacts()
  const needle = query.trim().toLowerCase()
  const localItems = useMemo(
    () =>
      needle
        ? contacts
            .filter((c) => matchesContact(c, needle))
            .sort((a, b) => collator.compare(contactDisplayName(a), contactDisplayName(b)))
        : [],
    [contacts, needle],
  )

  const myUserId = useAppStore((s) => s.me?.user.id)
  const config = useAppStore((s) => s.config)
  const rules = useMemo(() => usernameRules(config), [config])
  const globalQuery = usernameSearchQuery(query, rules)
  const globalSearch = useUsernameSearch(globalQuery)
  // Изменение v1.7: публичные каналы ищутся тем же запросом и стоят отдельной секцией.
  const channelSearch = useChannelSearch(globalQuery)
  // Найденные по никнейму, кто уже в контактах, не дублируются во второй секции
  const globalItems = useMemo(() => {
    const known = new Set(localItems.map((c) => c.id))
    return globalSearch.items.filter((item) => !known.has(item.id))
  }, [globalSearch.items, localItems])

  /*
   * Перед уходом к результату запрос записывается в адрес (`?q=`): «назад»
   * возвращает на тот же список, а не на пустое поле — как в Telegram.
   */
  const remember = (): Promise<void> =>
    navigate({ to: '/search', search: query.trim() ? { q: query } : {}, replace: true })

  const recent = useReplica(() => uiStateRepo.get<RecentEntry[]>(UiKey.searchRecent), [], null)

  const openContact = (contact: Contact): void => {
    void rememberRecent({
      id: contact.id,
      name: contactDisplayName(contact),
      username: contact.username ?? null,
    })
    void remember().then(() =>
      contact.conversationId
        ? navigate({ to: '/chats/$conversationId', params: { conversationId: contact.conversationId } })
        : navigate({ to: '/users/$userId', params: { userId: contact.id } }),
    )
  }

  const openUser = (userId: string, entry?: RecentEntry): void => {
    if (entry) void rememberRecent(entry)
    void remember().then(() => navigate({ to: '/users/$userId', params: { userId } }))
  }

  const clearRecent = (): void => void uiStateRepo.remove(UiKey.searchRecent)

  const nothing = needle && !localItems.length && !globalItems.length && !globalSearch.loading

  return (
    <Screen
      title="Поиск"
      back
      flush
      headerContent={
        <div className={cx(rows['searchWrap'], styles['field'])}>
          <SearchInput
            ref={inputRef}
            value={query}
            onValueChange={setQuery}
            placeholder={category === 'messages' ? 'Текст сообщения' : 'Имя, название или @никнейм'}
            aria-label="Поиск людей, чатов и сообщений"
            autoComplete="off"
            enterKeyHint="search"
            onClear={() => {
              setQuery('')
              inputRef.current?.focus({ preventScroll: true })
            }}
          />
        </div>
      }
    >
      <div className={roadmap['tabs']} role="tablist" aria-label="Тип результатов поиска">
        {(
          [
            { id: 'people', name: 'Люди' },
            { id: 'chats', name: 'Чаты' },
            { id: 'messages', name: 'Сообщения' },
          ] as const
        ).map((tab) => (
          <button
            type="button"
            key={tab.id}
            role="tab"
            aria-selected={category === tab.id}
            onClick={() => setCategory(tab.id)}
          >
            {tab.name}
          </button>
        ))}
      </div>
      {category === 'messages' ? (
        <>
          <div className={roadmap['filters']}>
            <label>
              Отправитель
              <select value={sender} onChange={(e) => setSender(e.target.value)}>
                <option value="">Все</option>
                <option value={useAppStore.getState().me?.user.id ?? ''}>Вы</option>
                {contacts.map((c) => (
                  <option key={c.id} value={c.id}>
                    {contactDisplayName(c)}
                  </option>
                ))}
              </select>
            </label>
            <label>
              С даты
              <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            </label>
            <label>
              По дату
              <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            </label>
            <label>
              Вложения
              <select value={attachment} onChange={(e) => setAttachment(e.target.value as typeof attachment)}>
                <option value="">Все сообщения</option>
                <option value="image">Фото</option>
                <option value="video">Видео</option>
                <option value="voice">Голосовые</option>
                <option value="file">Файлы</option>
                <option value="links">Ссылки</option>
              </select>
            </label>
          </div>
          {messageSearch.localOnly ? (
            <Banner tone="warning" icon="wifi-off">
              {messageSearch.failed ? 'Серверный поиск недоступен. ' : 'Нет сети. '}Показаны только сообщения,
              сохранённые на этом устройстве.
            </Banner>
          ) : null}
          <div className={roadmap['results']} aria-busy={messageSearch.loading}>
            {messageSearch.items.map((message) => (
              <button
                key={message.id}
                type="button"
                className={roadmap['result']}
                onClick={() =>
                  void remember().then(() =>
                    message.comment_of
                      ? navigate({
                          to: '/chats/$conversationId/posts/$postId',
                          params: { conversationId: message.conversation_id, postId: message.comment_of },
                          search: { seq: message.seq },
                        })
                      : message.topic
                        ? navigate({
                            to: '/chats/$conversationId/topics/$topicId',
                            params: { conversationId: message.conversation_id, topicId: message.topic.id },
                            search: { seq: message.seq },
                          })
                        : navigate({
                            to: '/chats/$conversationId',
                            params: { conversationId: message.conversation_id },
                            search: { seq: message.seq },
                          }),
                  )
                }
              >
                <Icon name={message.type === 'file' ? 'paperclip' : 'chats'} size={22} />
                <span className={roadmap['resultBody']}>
                  <strong>
                    {conversationNames.get(message.conversation_id) ?? 'Чат'}
                    {message.topic ? ` · #${message.topic.title}` : ''} ·{' '}
                    {message.sender?.display_name ?? (message.sender_id === myUserId ? 'Вы' : 'Сообщение')}
                  </strong>
                  <span>
                    {highlightSnippet(
                      message.text ?? message.media?.file_name ?? mediaLabel(message.type),
                      query,
                    )}
                  </span>
                  <small>{new Date(message.created_at).toLocaleString('ru-RU')}</small>
                </span>
              </button>
            ))}
          </div>
          {messageSearch.loading || messageSearch.hasMore || messageSearch.localOnly ? (
            <Button variant="ghost" loading={messageSearch.loading} onClick={messageSearch.loadMore}>
              {messageSearch.localOnly ? 'Повторить поиск' : 'Показать ещё'}
            </Button>
          ) : null}
          {!messageSearch.loading && !messageSearch.items.length ? (
            <EmptyState
              icon="search"
              title={
                query || attachment || sender || from || to ? 'Сообщений не найдено' : 'Поиск по всем чатам'
              }
              text="Введите слово или выберите фильтры."
            />
          ) : null}
        </>
      ) : null}
      {category === 'chats' ? (
        <div className={roadmap['results']}>
          {conversations
            .filter((c) => (faceOf(c).name ?? '').toLowerCase().includes(needle))
            .map((c) => (
              <button
                type="button"
                key={c.id}
                className={roadmap['result']}
                onClick={() =>
                  void remember().then(() =>
                    navigate({ to: '/chats/$conversationId', params: { conversationId: c.id } }),
                  )
                }
              >
                <Icon name="chats" size={24} />
                <span>{faceOf(c).name}</span>
              </button>
            ))}
          {channelSearch.items.map((c) => (
            <button
              type="button"
              className={roadmap['result']}
              key={c.id}
              onClick={() => void navigate({ to: '/channels/$channelId', params: { channelId: c.id } })}
            >
              <Icon name="people" size={24} />
              <span>{c.title}</span>
            </button>
          ))}
        </div>
      ) : null}
      <div className={rows['narrow']} hidden={category !== 'people'}>
        {!needle && recent?.length ? (
          <section aria-labelledby="search-recent">
            <div className={styles['sectionHead']}>
              <h2 id="search-recent" className={styles['sectionTitle']}>
                Недавние
              </h2>
              <Button variant="ghost" size="sm" onClick={clearRecent}>
                Очистить
              </Button>
            </div>
            <div className={rows['list']} {...{ [KEY_LIST_ATTR]: true }}>
              {recent.map((entry, index) => (
                <RecentResult
                  key={entry.id}
                  entry={entry}
                  divider={index > 0}
                  onOpen={() => openUser(entry.id, entry)}
                />
              ))}
            </div>
          </section>
        ) : null}

        {localItems.length ? (
          <section aria-labelledby="search-contacts">
            <h2 id="search-contacts" className={styles['sectionTitle']}>
              Контакты
            </h2>
            <div className={rows['list']} {...{ [KEY_LIST_ATTR]: true }}>
              {localItems.map((contact, index) => (
                <ContactResult key={contact.id} contact={contact} divider={index > 0} onOpen={openContact} />
              ))}
            </div>
          </section>
        ) : null}

        {/*
          Секция глобального поиска нужна, только когда ей есть что сказать: если
          человек уже нашёлся среди контактов, строка «никого не нашлось» рядом
          с найденным сбивает с толку.
        */}
        {globalQuery && (globalItems.length || globalSearch.loading || !localItems.length) ? (
          <section aria-labelledby="search-global">
            <h2 id="search-global" className={styles['sectionTitle']}>
              Глобальный поиск
            </h2>
            {globalItems.length ? (
              <div className={rows['list']} {...{ [KEY_LIST_ATTR]: true }}>
                {globalItems.map((item, index) => (
                  <GlobalResult
                    key={item.id}
                    item={item}
                    query={globalQuery}
                    divider={index > 0}
                    onOpen={(id) =>
                      openUser(id, {
                        id: item.id,
                        name: item.display_name ?? 'Пользователь',
                        username: item.username,
                      })
                    }
                  />
                ))}
              </div>
            ) : globalSearch.loading ? (
              <div className={rows['list']} aria-busy="true" aria-label="Ищем людей по никнейму">
                {[0, 1, 2].map((index) => (
                  <div key={index} className={cx(rows['row'], styles['skeletonRow'])} aria-hidden>
                    <span className={styles['skeletonAvatar']} />
                    <span className={rows['rowBody']}>
                      <span className={styles['skeletonLine']} style={{ width: `${56 - index * 10}%` }} />
                      <span
                        className={cx(styles['skeletonLine'], styles['skeletonLineThin'])}
                        style={{ width: `${38 + index * 8}%` }}
                      />
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className={styles['hint']}>Никого с таким никнеймом не нашлось</p>
            )}
          </section>
        ) : null}

        {globalQuery && channelSearch.items.length ? (
          <section aria-labelledby="search-channels">
            <h2 id="search-channels" className={styles['sectionTitle']}>
              Каналы и группы
            </h2>
            <div className={rows['list']}>
              {channelSearch.items.map((channel, index) => (
                <button
                  key={channel.id}
                  type="button"
                  className={cx(rows['row'], index > 0 && rows['rowDivider'])}
                  onClick={() => {
                    void remember()
                    void navigate({ to: '/channels/$channelId', params: { channelId: channel.id } })
                  }}
                >
                  <Avatar name={channel.title} photo={channel.photo ?? null} size="lg" />
                  <span className={rows['rowBody']}>
                    <span className={rows['rowName']}>{channel.title}</span>
                    <span className={rows['rowStatus']}>
                      {channel.username ? `@${channel.username} · ` : ''}
                      {subscribersLabel(channel.subscribers_count, channel.format)}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          </section>
        ) : null}

        {nothing && !globalQuery ? (
          <EmptyState
            icon="search"
            title="Ничего не нашлось"
            text={`Людей вне контактов ищем по никнейму — от ${rules.searchMin} символов.`}
          />
        ) : null}
      </div>
    </Screen>
  )
}

/* ===================================================================== */
/* Строки результатов                                                     */
/* ===================================================================== */

/** Строка «Недавних»: фото берём из контактов, если человек там есть. */
function RecentResult({
  entry,
  divider,
  onOpen,
}: {
  entry: RecentEntry
  divider: boolean
  onOpen: () => void
}) {
  const { items } = useContacts()
  const known = items.find((contact) => contact.id === entry.id) ?? null

  return (
    <button
      type="button"
      className={cx(rows['row'], styles['resultRow'], divider && rows['rowDivider'])}
      {...{ [KEY_ITEM_ATTR]: true }}
      onClick={onOpen}
    >
      <Avatar name={entry.name} photo={known?.photo ?? null} availability="available" size="lg" />
      <span className={rows['rowBody']}>
        <span className={rows['rowName']}>{entry.name}</span>
        {entry.username ? <span className={rows['rowStatus']}>@{entry.username}</span> : null}
      </span>
    </button>
  )
}

function ContactResult({
  contact,
  divider,
  onOpen,
}: {
  contact: Contact
  divider: boolean
  onOpen: (contact: Contact) => void
}) {
  const presence = useLivePresence(contact.id, contact.presence)
  const status =
    (contact.availability === 'available' ? formatPresence(presence) : null) ??
    (contact.username ? `@${contact.username}` : '')

  return (
    <button
      type="button"
      className={cx(rows['row'], styles['resultRow'], divider && rows['rowDivider'])}
      {...{ [KEY_ITEM_ATTR]: true }}
      onClick={() => onOpen(contact)}
    >
      <Avatar
        name={contact.name}
        photo={contact.photo}
        availability={contact.availability}
        size="lg"
        online={presence?.online ?? false}
      />
      <span className={rows['rowBody']}>
        <span className={rows['rowName']}>{contactDisplayName(contact)}</span>
        {status ? (
          <span className={cx(rows['rowStatus'], presence?.online && rows['rowStatusOnline'])}>{status}</span>
        ) : null}
      </span>
    </button>
  )
}

function GlobalResult({
  item,
  query,
  divider,
  onOpen,
}: {
  item: UserSearchResult
  query: string
  divider: boolean
  onOpen: (userId: string) => void
}) {
  const name = item.display_name ?? 'Пользователь'
  // Совпавшее начало никнейма выделено — так видно, почему строка нашлась (как в Telegram)
  const matched = item.username.toLowerCase().startsWith(query.toLowerCase()) ? query.length : 0

  return (
    <button
      type="button"
      className={cx(rows['row'], styles['resultRow'], divider && rows['rowDivider'])}
      {...{ [KEY_ITEM_ATTR]: true }}
      aria-label={`${name}, @${item.username}`}
      onClick={() => onOpen(item.id)}
    >
      <Avatar name={item.display_name} photo={item.photo} availability="available" size="lg" />
      <span className={rows['rowBody']}>
        <span className={rows['rowName']}>
          {name}
          {item.age !== null ? `, ${item.age}` : ''}
        </span>
        <span className={rows['rowStatus']}>
          @<span className={styles['matched']}>{item.username.slice(0, matched)}</span>
          {item.username.slice(matched)}
          {item.city ? ` · ${item.city.name}` : ''}
        </span>
      </span>
    </button>
  )
}

function highlightSnippet(text: string, query: string) {
  const needle = query.trim()
  const index = text.toLocaleLowerCase().indexOf(needle.toLocaleLowerCase())
  if (!needle || index < 0) return text.slice(0, 200)
  const start = Math.max(0, index - 70)
  return (
    <>
      {start ? '…' : ''}
      {text.slice(start, index)}
      <mark>{text.slice(index, index + needle.length)}</mark>
      {text.slice(index + needle.length, index + needle.length + 120)}
      {text.length > index + needle.length + 120 ? '…' : ''}
    </>
  )
}
